"use client";

import { useCallback, useSyncExternalStore } from "react";

/** Whether a CSS media query currently matches.
 *
 *  For the cases CSS alone can't cover — here, NOT MOUNTING a component at all
 *  below a breakpoint. Hiding it with `hidden lg:block` would still download
 *  its chunk and keep its render loop running behind a `display: none`, which
 *  is exactly the cost we're trying to avoid on the devices least able to
 *  absorb it. Anything that can be expressed as a plain Tailwind variant
 *  should be — reach for this only when mounting is the thing to control.
 *
 *  Always `false` during SSR, so a gated subtree must be safe to omit from the
 *  server-rendered HTML (i.e. already `ssr: false`, or decorative). Never gate
 *  content a crawler needs to read on this.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
